import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiGlobe, FiSave } from 'react-icons/fi';
import Layout from '../components/Layout';

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';

const SAVE_PIN = '795842';

const CONFIG_FIELDS = [
    {
        name: 'SGVPInviteLink',
        label: 'SGVP Invite Link',
        type: 'url',
        placeholder: 'https://t.me/+xxxxxxxx',
        note: 'Telegram invite link for SGVP.',
        noteTitle: 'SGVP Invite Link',
    },
    {
        name: 'SGQuitSmokingInviteLink',
        label: 'SGQuitSmoking Invite Link',
        type: 'url',
        placeholder: 'https://t.me/+xxxxxxxx',
        note: 'Telegram invite link for SGQuitSmoking.',
        noteTitle: 'SGQuitSmoking Invite Link',
    },
];

const SAVE_FIELD_NOTES = CONFIG_FIELDS.filter((field) => field.note).map((field) => ({
    label: field.noteTitle || field.label,
    text: field.note,
}));

const SimonConfigPage = () => {
    const [formData, setFormData] = useState({
        SGVPInviteLink: '',
        SGQuitSmokingInviteLink: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [pinInput, setPinInput] = useState('');
    const [pinError, setPinError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/simon-config`,
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setFormData({
                    SGVPInviteLink: res.data.data?.SGVPInviteLink || '',
                    SGQuitSmokingInviteLink: res.data.data?.SGQuitSmokingInviteLink || '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load config');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setSuccess('');
    };

    const performSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_API_URL}/api/simon-config`,
                {
                    SGVPInviteLink: formData.SGVPInviteLink.trim(),
                    SGQuitSmokingInviteLink: formData.SGQuitSmokingInviteLink.trim(),
                },
                { headers: authHeaders() }
            );
            if (res.data.success) {
                setFormData({
                    SGVPInviteLink: res.data.data?.SGVPInviteLink || '',
                    SGQuitSmokingInviteLink: res.data.data?.SGQuitSmokingInviteLink || '',
                });
                setSuccess('Simon config saved.');
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to save config');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setPinInput('');
        setPinError('');
        setShowSaveModal(true);
    };

    const handleCloseSaveModal = () => {
        if (saving) return;
        setShowSaveModal(false);
        setPinInput('');
        setPinError('');
    };

    const handleConfirmSave = async () => {
        if (pinInput.trim() !== SAVE_PIN) {
            setPinError('Pin wrong.');
            return;
        }
        setShowSaveModal(false);
        setPinInput('');
        setPinError('');
        await performSave();
    };

    return (
        <Layout>
            <div className="px-4 py-6 max-w-6xl">
                <div className="flex items-center gap-2 mb-6">
                    <FiGlobe className="h-6 w-6 text-blue-500" />
                    <h1 className="text-2xl font-bold">Simon Config</h1>
                </div>

                <p className="text-gray-600 mb-6">
                    Telegram invite links for Simon groups. Saving requires a pin.
                </p>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}
                {success && (
                    <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-md">{success}</div>
                )}

                {loading ? (
                    <p className="text-gray-600">Loading...</p>
                ) : (
                    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow border border-gray-200 p-6 space-y-6">
                        {CONFIG_FIELDS.map((field) => (
                            <div
                                key={field.name}
                                className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-2 items-start"
                            >
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {field.label}
                                    </label>
                                    <input
                                        type={field.type}
                                        name={field.name}
                                        value={formData[field.name]}
                                        onChange={handleChange}
                                        placeholder={field.placeholder}
                                        className={inputClass}
                                    />
                                </div>
                                <div className="lg:pt-7">
                                    {field.note ? (
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                            <span className="font-semibold text-gray-800">
                                                {field.noteTitle || field.label}
                                            </span>
                                            {' — '}
                                            {field.note}
                                        </p>
                                    ) : (
                                        <span className="hidden lg:block" aria-hidden="true" />
                                    )}
                                </div>
                            </div>
                        ))}
                        <div className="flex justify-end">
                            <button
                                type="submit"
                                disabled={saving}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                )}

                {showSaveModal && (
                    <div className="fixed inset-0 z-[200] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                        <div className="relative top-10 mx-auto p-6 border w-full max-w-xl shadow-lg rounded-md bg-white mb-10">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm save</h3>
                            <ul className="space-y-3 text-sm text-gray-700 mb-6">
                                {SAVE_FIELD_NOTES.map((item) => (
                                    <li key={item.label}>
                                        <span className="font-semibold text-gray-900">{item.label}</span>
                                        {' — '}
                                        {item.text}
                                    </li>
                                ))}
                            </ul>
                            <div>
                                <label htmlFor="simon-config-save-pin" className="block text-sm font-medium text-gray-700 mb-1">
                                    Pin
                                </label>
                                <input
                                    id="simon-config-save-pin"
                                    type="password"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    value={pinInput}
                                    onChange={(e) => {
                                        setPinInput(e.target.value);
                                        setPinError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirmSave();
                                        }
                                    }}
                                    className={inputClass}
                                    placeholder="Enter pin"
                                />
                                {pinError && (
                                    <p className="mt-2 text-sm text-red-600">{pinError}</p>
                                )}
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={handleCloseSaveModal}
                                    disabled={saving}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmSave}
                                    disabled={saving}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : 'OK'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default SimonConfigPage;
